import React, { useEffect, useState } from "react";
import { ICourse, IClub, ICourseSummary } from "@eagerdog/interfaces";
import { Helmet } from "react-helmet";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Constants, COURSE_CATEGORY_OPTIONS, getCourseCategoryValue } from "@eagerdog/constants";


import { helperService } from "src/services/helper.service";
import { apiService } from "src/services";
import Button from "src/components/Button/Button";
import { Modal, useModal } from "src/components/Modal/Modal";
import CourseCard from "src/components/CourseCard/CourseCard";
import SearchFilter, { IActiveFilter, ISearchOption } from "src/components/SearchFilter/SearchFilter";
import StripeOnboardingModal from "src/components/StripeOnboardingModal/StripeOnboardingModal";

import CreateCourse from "./CreateCourse/CreateCourse";

import styles from "./Courses.module.scss";

interface IProps {

}

const Courses: React.FC<IProps> = (props) => {
    const user = helperService.getUser();
    const [currentTab, setCurrentTab] = useState<number>(0);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [dateFilter] = useState<Date | null>(() => {
        const d = searchParams.get("date");
        return d ? new Date(d) : null;
    });

    const [loading, setLoading] = useState<boolean>(true);
    const [courses, setCourses] = useState<any[]>([]);
    const [currentClub, setCurrentClub] = useState<IClub | null>(null);

    const limit:number = 12;
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [skip, setSkip] = useState<number>(0);

    const searchOptions: ISearchOption[] = [
        {
            label: "Start Date",
            value: "start_date",
            helpText: "Start Date of the Course"
        },
        {
            label: "Category",
            value: "category",
            helpText: "Discipline or sport category",
            options: COURSE_CATEGORY_OPTIONS,
        },
    ];

    const [searchFilters, setSearchFilters] = useState<IActiveFilter[]>([]);
    const [modalContent, setModalContent] = useState<string>("");

    const [searchReset, setSearchReset] = useState<boolean>(false);
    
    const { isShown, toggle } = useModal();

    const getModalContent = () => {
        let content = <></>;

        switch (modalContent) {
            case "stripeOnboarding":
                const club = helperService.getClub();
                content = (
                    <StripeOnboardingModal
                        clubId={club._id}
                        onCancel={() => {
                            toggle();
                            setModalContent("");
                        }}
                    />
                );
                break;
            case "createCourse":
                content = <CreateCourse
                    onSubmit={(newCourse?: ICourse) => {
                        if (newCourse) {
                            navigate("/c/" + newCourse.handle);
                        }
                    }}
                />;
                break;
        }   

        return content;
    }

    const getCourses = () => {
        setLoading(true);

        let query: any = {
            sort: [
                {
                    "attribute_name": "start_date",
                    "sort": "asc"
                }
            ],
            $limit: limit,
            $skip: skip,
            query: {
                $and: [{
                    attribute_name: "status",
                    attribute_value: {
                        operator: "$ne",
                        value: Constants.course_status.archived,
                    },
                }],
                $or: []
            }
        };

        for (const filter of searchFilters) {
            let newQuery: any = {
                attribute_name: filter.filter.value,
                attribute_value: {
                    operator: (filter.filter.value === "start_date" || filter.filter.value === "end_date") ? "$gte"
                        : filter.filter.value === "category" ? "$eq"
                        : "$regex",
                    value: filter.filter.value === "category"
                        ? getCourseCategoryValue(filter.option)
                        : filter.option
                }
            };

            if (filter.filter.value && filter.option) {
                query.query.$or!.push(newQuery);
            }
        }

        if (dateFilter) {
            query.query.$and!.push({
                attribute_name: "start_date",
                attribute_value: { operator: "$gte", value: dateFilter }
            });
        }

        if (currentTab === 0) {
            query.query.$and!.push({
            attribute_name: "end_date",
            attribute_value: {
                operator: "$gte",
                value: new Date(),
            },
            });
        } else if (currentTab === 1) {
            query.query.$and!.push({
            attribute_name: "end_date",
            attribute_value: {
                operator: "$lte",
                value: new Date()
            }
            });

            query.sort =  [
                {
                    "attribute_name": "start_date",
                    "sort": "desc"
                }
            ];
        }

        if (user.user_type === Constants.user_type.club_manager) {
            // Don't fetch until we have the club
            if (!currentClub) {
                setLoading(false);
                return;
            }
            apiService.getPrivateCourses(query, currentClub._id).then((response) => {
                setCourses((prevCourses) => {
                    const newCourses = skip === 0 ? response : [...prevCourses, ...response];
                    setHasMore(response.length === limit);
                    setSkip(skip + limit);
                    return newCourses;
                });
            }).finally(() => {
                setLoading(false);
            });
        } else {
            apiService.getCourses(query).then((response) => {
                setCourses((prevCourses) => {
                    const newCourses = skip === 0 ? response : [...prevCourses, ...response];
                    setHasMore(response.length === limit);
                    setSkip(skip + limit);
                    return newCourses;
                });
            }).finally(() => {
                setLoading(false);
            });
        }
    }

    const resetSearch = () => {
      setSkip(0);
      setHasMore(true);
      setCourses([]);
      setSearchReset(true);
    }

    // Load the club for club managers (mirrors the Events page pattern)
    useEffect(() => {
      if (user.user_type === Constants.user_type.club_manager) {
        apiService.getCurrentClub().then((club) => {
          setCurrentClub(club);
          setSearchReset(true);
        }).catch(() => {
          setLoading(false);
        });
      } else {
        setSearchReset(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.user_type]);

    useEffect(() => {
      resetSearch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFilters]);

    useEffect(() => {
      if (searchReset) {
        getCourses();
        setSearchReset(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    } , [searchReset]);

    useEffect(() => {
      setSkip(0);
      setHasMore(true);
      setCourses([]);
      setSearchReset(true);
    }, [currentTab]);

    return (
        <div className={styles.Courses}>
            <Helmet>
                <title>Courses &amp; Classes</title>
                <meta name="description" content="Upcoming Courses and Classes from kennel clubs on Eager Dog." />
                <meta name="robots" content="index, follow" />
            </Helmet>
            <div className={styles.inner}>
                <div className={styles.title}>
                    <h1>Courses &amp; Classes</h1>
                    <SearchFilter
                        searchOptions={searchOptions}
                        onFilterChange={(filters: IActiveFilter[]) => {
                            setSearchFilters(filters);
                        }}
                        initialFilters={dateFilter ? [{ filter: { label: "Start Date", value: "start_date", helpText: "Start Date of the Course" }, option: (searchParams.get("date") ?? "") + "T00:00:00" }] : []}
                    />
                </div>
                {user.user_type === "club_manager" && (
                <div className={styles.listOptions}>
                    <span className={currentTab === 0 ? styles.active : ""} onClick={() => { setCurrentTab(0); }}>Upcoming</span>
                    <span className={currentTab === 1 ? styles.active : ""} onClick={() => { setCurrentTab(1); }}>Past</span>
                </div>
                )}
                {loading && <div className={styles.loading} key={0}>
                    <div className={styles.list}>
                        <div className={styles.cardSkeleton}></div>
                        <div className={styles.cardSkeleton}></div>
                        <div className={styles.cardSkeleton}></div>
                        <div className={styles.cardSkeleton}></div>
                        <div className={styles.cardSkeleton}></div>
                        <div className={styles.cardSkeleton}></div>
                    </div>
                </div>}
                {((!loading && skip === 0) || skip > 0) && (<InfiniteScroll
                    className={styles.list}
                    dataLength={courses.length}
                    next={getCourses}
                    hasMore={hasMore}
                    loader={<div className={styles.loading} key={0}></div>}
                >
                {courses.length === 0 ? user.user_type === Constants.user_type.club_manager ? (<div className={styles.noCourses}>
                        <div className={styles.empty}>
                            <span className={styles.emptyMsg}>You don't have any courses planned, yet.</span>
                            <Button className={styles.create} onClick={async () => { 
                                const club = helperService.getClub();
                                if (club._id) {
                                    try {
                                        const status = await apiService.getAccountStatus(club._id);
                                        if (status.onboarding_status !== "requirements_met" || !status.charges_enabled) {
                                            setModalContent("stripeOnboarding");
                                            toggle();
                                            return;
                                        }
                                    } catch (err) {
                                        console.error("Failed to fetch account status:", err);
                                    }
                                }
                                setModalContent("createCourse"); 
                                toggle(); 
                            }}>Create a Course</Button>
                        </div>
                    </div>) : ((user.user_type === Constants.user_type.owner || user.user_type === "") && courses.length === 0 && !loading && (<div className={styles.noCourses}>
                        <div className={styles.empty}>
                            <span className={styles.emptyMsg}>No courses planned, yet.</span>
                        </div>
                    </div>)
                ) : (
                    courses.map((course: ICourseSummary, i: number) => {
                        return(
                            <CourseCard
                                isAdmin={true}
                                course={course}  
                                key={"course-" + i}
                            />
                        );
                    })
                )}
                {user?.user_type === Constants.user_type.club_manager && courses.length > 0 && <div onClick={async () => { 
                    const club = helperService.getClub();
                    if (club._id) {
                        try {
                            const status = await apiService.getAccountStatus(club._id);
                            if (status.onboarding_status !== "requirements_met" || !status.charges_enabled) {
                                setModalContent("stripeOnboarding");
                                toggle();
                                return;
                            }
                        } catch (err) {
                            console.error("Failed to fetch account status:", err);
                        }
                    }
                    setModalContent("createCourse"); 
                    toggle(); 
                }} className={styles.createCard}>
                    <div className={`${styles.icon} ${styles.plus}`}></div>
                    <span>Create a Course</span>
                </div>}
                {(user?.user_type === Constants.user_type.club_manager ? (courses.length + 1) : courses.length) % 3 !== 0 && <div className={styles.cardPlaceholder}></div>}
                </InfiniteScroll>)}
            </div>
            <Modal
                className={modalContent === "createCourse" ? "createCourseModal" : modalContent}
                isShown={isShown}
                hide={() => {
                    toggle();
                    setSkip(0);
                    setHasMore(true);
                    setCourses([]);
                    setSearchReset(true);
                }}
                modalContent={getModalContent()}
            />
        </div>
    );
};

export default Courses;